/**
 * Add to Cart component
 *
 * @module addtocart
 * @version 2.0.0
 * @extends HTMLElement
 */

customElements.component = "component-addtocart";

if (!customElements.get(customElements.component)) {
  class addToCartComponent extends HTMLElement {
    /**
     * Add to Cart web component.
     *
     * @constructor
     */
    constructor() {
      super();
      this.currentVariant = null;
      this.currentQuantity = 1;

      if (this.dataset.handle) {
        window.theme.product
          .loadProduct(this.dataset.handle, true)
          .then((product) => {
            this.initSubmitButton(product);
            this.initEvents(product);
          });
      } else {
        console.log("Missing product handle", this);
      }
    }

    /**
     * Init component events.
     *
     * @param {object} product Product data.
     * @returns {undefined}
     */
    initEvents(product) {
      // On submit add to cart form

      const form = this.getForm();

      if (form) {
        form.addEventListener("submit", (event) => this.onFormSubmit(event));
      }

      // Initialize the state with default values.
      this.currentVariant = product.variants[0]; // Assume first variant is default
      this.currentQuantity = 1; // Default quantity

      // Update the price display based on the initial variant and quantity.
      this.updatePrice(this.currentVariant, this.currentQuantity);

      // On variant selector ready

      const variantSelector = this.querySelector(
        'variant-selector[data-handle="' + this.dataset.handle + '"]'
      );

      if (variantSelector) {
        variantSelector.addEventListener("ready", (event) =>
          this.OnVariantSelectorReady(event, product)
        );

        if (variantSelector.classList.contains("ready")) {
          this.OnVariantSelectorChange(
            variantSelector.querySelector("select"),
            product
          );
        }
      }
      // Listen for variant change
      window.addEventListener("variant-change", (event) => {
        if (event.detail.product.handle == this.dataset.handle) {
          // Store the current variant
          this.currentVariant = event.detail.variant;
          // Update the price with the stored quantity
          this.updatePrice(this.currentVariant, this.currentQuantity);
        }
      });

      // Listen for quantity-change event
      this.addEventListener("quantity-change", (event) => {
        if (event.detail.handle === this.dataset.handle) {
          const quantity = event.detail.value; // Get the new quantity from the event

          // Make sure the quantity is a number
          if (!isNaN(quantity)) {
            // Store the current quantity
            this.currentQuantity = quantity;

            // If there's a current variant, update the price with the new quantity
            if (this.currentVariant) {
              this.updatePrice(this.currentVariant, quantity);
            }
          }
        }
      });
    }

    /**
     * Toggle button if product select variant is out of stock.
     *
     * @param {object} select Product select variant.
     * @returns {undefined}
     */
    toggleButton(select) {
      let optionSelected = select.children[select.selectedIndex],
        outOfStockContent = this.querySelector("[data-outofstock-content]"),
        btn = this.getSubmitButton();

      if (optionSelected) {
        if (optionSelected.dataset.disabled === "") {
          optionSelected = false;
        } else {
          optionSelected = true;
        }
      } else {
        optionSelected = false;
      }

      if (optionSelected) {
        outOfStockContent.classList.remove("active");
        btn.disabled = false;
      } else {
        outOfStockContent.classList.add("active");
        btn.disabled = true;
      }
    }

    /**
     * Get component template
     *
     * @returns {string} Component HTML template.
     */
    getTemplate() {
      return window.theme.addToCart.template
        ? window.theme.addToCart.template
        : "";
    }

    /**
     * Init add to cart button features
     *
     * @param {object} product Product data.
     * @returns {undefined}
     */
    initSubmitButton(product) {
      let html = this.getTemplate(),
        btn = this.getSubmitButton();

      if (html && btn) {
        btn.classList.add("product-addtocart__button");
        btn.classList.add("btn");
        btn.classList.add("btn-icon-after");
        btn.classList.add("btn-loading");

        if (product.available) {
          btn.classList.add("active");
        } else {
          btn.disabled = true;
        }

        // Don't overwrite custom button label (upsell popup)
        if (btn.dataset.keepLabel === "true") return;

        // Inject HTML
        btn.innerHTML = html;
      }
    }

    /**
     * Get add to cart form
     *
     * @returns {object} Add to cart form.
     */
    getForm() {
      return this.querySelector("form");
    }

    /**
     * Get submit button
     *
     * @returns {object} Submit button.
     */
    getSubmitButton() {
      return this.querySelector('button[type="submit"]');
    }

    /**
     * On form submit
     *
     * @param {object} event Event data.
     * @returns {undefined}
     */
    onFormSubmit(event) {
      const form = event.target;

      event.preventDefault();

      let button = form.querySelector('[type="submit"]'),
        validate = this.validateFieldRequired(form),
        message = this.getErrorMessageElement();

      if (!validate) {
        button.setAttribute("disabled", true);

        window.theme.cart
          .addByForm(form)
          .then((result) => {
            button.removeAttribute("disabled");

            message.innerHTML = "";
            message.classList.remove("enabled");
          })
          .catch((error) => {
            console.log(error);
            button.removeAttribute("disabled");

            message.innerHTML = "";

            message.append(error.message);
            message.classList.add("enabled");
          });
      }
    }

    /**
     * Init component events.
     *
     * @param {object} product Product data.
     * @returns {undefined}
     */
    OnVariantSelectorReady(event, product) {
      let select = event.target.querySelector("select");

      // Set the current variant based on the select element's initial value.
      let initialVariantIndex = select.selectedIndex;
      this.currentVariant = product.variants[initialVariantIndex];

      // Check if a quantity has already been selected (e.g., from a previous page load).
      let quantityInput = this.querySelector("[data-quantity-input]");
      if (quantityInput && !isNaN(quantityInput.value)) {
        this.currentQuantity = parseInt(quantityInput.value);
      }

      // Now update the price display.
      this.updatePrice(this.currentVariant, this.currentQuantity);

      this.OnVariantSelectorChange(select, product);
    }

    /**
     * On variant selector change.
     *
     * @param {object} select Variant selector select element.
     * @param {object} product Product data.
     * @returns {undefined}
     */
    OnVariantSelectorChange(select, product) {
      this.toggleButton(select, product);

      // Check if the select variant changes
      select.addEventListener("change", (event) =>
        this.toggleButton(event.target, product)
      );
    }

    /**
     * On variant selector change.
     *
     * @param {object} form Form element.
     * @returns {boolean}
     */
    validateFieldRequired(form) {
      const fields = form.querySelectorAll("[required]");

      let foundError = false;

      for (let field of fields) {
        foundError = field.validity.valueMissing;

        if (foundError) {
          field.classList.add("invalid");
        }
      }

      return foundError;
    }

    /**
     * Get error message element.
     *
     * @returns {object} Error message element.
     */
    getErrorMessageElement() {
      let element = this.querySelector("[data-error-message]");

      if (!element) {
        element = document.createElement("div");
        element.dataset.errorMessage = "";

        this.append(element);
      }

      return element;
    }

    /**
     * Update the price in the Add to Cart section.
     *
     * @param {object} variant Variant data with pricing information.
     * @param {number} quantity The quantity of the product to price.
     * @returns {undefined}
     */
    updatePrice(variant, quantity = this.currentQuantity) {
      // Only select price elements within this component instance
      const priceInAtcElements = this.querySelectorAll("[data-priceinatc]");

      if (priceInAtcElements.length && variant) {
        const totalPrice = variant.price * quantity;
        priceInAtcElements.forEach((elem) => {
          elem.innerHTML = theme.money.format(totalPrice);
        });
      } else {
        console.warn("Element with data-priceinatc not found within this component or no variant selected.");
      }
    }

  }

  customElements.define(customElements.component, addToCartComponent);
}
